import React, { useState } from 'react';
import { Icon, useTheme, type ThemeMode, H1 } from '@tviklax/ui-components';
import { usePreferences, Currency, Language } from '../../../context/PreferencesContext';
import { useRole, type Role } from '../../../context/RoleContext';
import {
    PageContainer,
    PageHeader,
    Breadcrumbs,
    SettingsCard,
    TabsContainer,
    Tab,
    CardContent,
    SectionTitle,
    AvatarUploadSection,
    AvatarPreview,
    UploadInfo,
    FormField,
    Label,
    Select,
    HelperText,
} from './ProfileSettings.styles';

export const ProfileSettings: React.FC = () => {
    const { currency, setCurrency, language, setLanguage, currencyNames, languageNames } = usePreferences();
    const { role: currentRole, roleLabel, switchRole } = useRole();
    const { mode: themeMode, setMode: setThemeMode, isDark } = useTheme();
    const [activeTab, setActiveTab] = useState<'profile' | 'preferences' | 'role-testing'>('profile');

    return (
        <PageContainer>
            <PageHeader>
                <H1 style={{ margin: '0' }}>Profile Settings</H1>
                <Breadcrumbs>
                    Home <Icon name="ChevronRight" size={14} /> <span>Profile Settings</span>
                </Breadcrumbs>
            </PageHeader>

            <SettingsCard>
                <TabsContainer>
                    <Tab $isActive={activeTab === 'profile'} onClick={() => setActiveTab('profile')}>
                        <Icon name="Image" size={18} />
                        Profile Picture
                    </Tab>
                    <Tab $isActive={activeTab === 'preferences'} onClick={() => setActiveTab('preferences')}>
                        <Icon name="Settings" size={18} />
                        Preferences
                    </Tab>
                    <Tab $isActive={activeTab === 'role-testing'} onClick={() => setActiveTab('role-testing')}>
                        <Icon name="Shield" size={18} />
                        Role Testing
                    </Tab>
                </TabsContainer>

                <CardContent>
                    {activeTab === 'profile' ? (
                        <>
                            <SectionTitle>Change Profile Picture</SectionTitle>

                            <AvatarUploadSection>
                                <AvatarPreview>
                                    <img
                                        src="https://images.unsplash.com/photo-1599566150163-29194dcaad36?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80"
                                        alt="Profile"
                                    />
                                </AvatarPreview>
                                <UploadInfo>
                                    Allowed JPG, JPEG or PNG. Max size of 2MB
                                </UploadInfo>
                            </AvatarUploadSection>
                        </>
                    ) : activeTab === 'preferences' ? (
                        <>
                            <SectionTitle>Preferences</SectionTitle>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                {/* Currency Selection */}
                                <FormField>
                                    <Label>Currency</Label>
                                    <Select
                                        value={currency}
                                        onChange={(e) => setCurrency(e.target.value as Currency)}
                                    >
                                        {Object.entries(currencyNames).map(([code, name]) => (
                                            <option key={code} value={code}>
                                                {code} - {name}
                                            </option>
                                        ))}
                                    </Select>
                                    <HelperText>
                                        Select your preferred currency for displaying prices across the app.
                                    </HelperText>
                                </FormField>

                                {/* Language Selection */}
                                <FormField>
                                    <Label>Language</Label>
                                    <Select
                                        value={language}
                                        onChange={(e) => {
                                            void setLanguage(e.target.value as Language);
                                        }}
                                    >
                                        {Object.entries(languageNames).map(([code, name]) => (
                                            <option key={code} value={code}>
                                                {name}
                                            </option>
                                        ))}
                                    </Select>
                                    <HelperText>
                                        Select your preferred language for the application interface.
                                    </HelperText>
                                </FormField>

                                {/* Theme Selection */}
                                <FormField>
                                    <Label>Theme</Label>
                                    <Select
                                        value={themeMode}
                                        onChange={(e) => setThemeMode(e.target.value as ThemeMode)}
                                    >
                                        <option value="light">☀️ Light</option>
                                        <option value="dark">🌙 Dark</option>
                                        <option value="auto">🔄 Auto (System)</option>
                                    </Select>
                                    <HelperText>
                                        {themeMode === 'auto' 
                                            ? `Auto mode follows your system preference. Currently: ${isDark ? 'Dark' : 'Light'}`
                                            : `Currently using ${themeMode} theme`}
                                    </HelperText>
                                </FormField>
                            </div>
                        </>
                    ) : activeTab === 'role-testing' ? (
                        <>
                            <SectionTitle>Role Testing (Development Only)</SectionTitle>
                            <div style={{ 
                                background: '#fef3c7', 
                                border: '1px solid #fbbf24', 
                                borderRadius: '8px', 
                                padding: '12px 16px', 
                                marginBottom: '24px',
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'flex-start'
                            }}>
                                <span style={{ fontSize: '16px' }}>⚠️</span>
                                <div style={{ fontSize: '13px', color: '#92400e' }}>
                                    <strong>Testing Mode:</strong> This role selector is temporary for development and will be removed in production. Roles will be set at login.
                                </div>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <label style={{ fontSize: '14px', fontWeight: 600, color: '#1f2937' }}>
                                        Current Role
                                    </label>
                                    <select
                                        value={currentRole}
                                        onChange={(e) => switchRole(e.target.value as Role)}
                                        style={{
                                            padding: '10px 12px',
                                            borderRadius: '6px',
                                            border: '1px solid #d1d5db',
                                            fontSize: '14px',
                                            color: '#374151',
                                            backgroundColor: 'white',
                                            cursor: 'pointer',
                                            maxWidth: '300px',
                                        }}
                                    >
                                        <option value="admin">Admin - Business Owner</option>
                                        <option value="ops">Ops - Logistics / Execution</option>
                                        <option value="compliance">Compliance - Customs & Docs</option>
                                        <option value="finance">Finance - Payments & Invoices</option>
                                        <option value="sales">Sales - Front-office</option>
                                        <option value="agent">Agent - Tvikla Authorized</option>
                                        <option value="customer">Customer - Buyer</option>
                                        <option value="supplier">Supplier - Seller / Manufacturer</option>
                                    </select>
                                    <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>
                                        Currently viewing as: <strong>{roleLabel}</strong>. Switch roles to test different permission levels.
                                    </p>
                                </div>

                                <div style={{ 
                                    background: '#f9fafb', 
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px', 
                                    padding: '16px',
                                    fontSize: '13px',
                                    color: '#4b5563',
                                    lineHeight: '1.6'
                                }}>
                                    <strong style={{ display: 'block', marginBottom: '8px', color: '#111827' }}>Role Permissions:</strong>
                                    {currentRole === 'admin' && '✅ Full access to all features including Access Control'}
                                    {currentRole === 'ops' && '✅ Manage shipments, scheduling, view payments/customs'}
                                    {currentRole === 'compliance' && '✅ Respond to customs queries, upload documents'}
                                    {currentRole === 'finance' && '✅ Process payments, approve invoices, view reports'}
                                    {currentRole === 'sales' && '✅ Create orders, view shipments, limited dashboard'}
                                    {currentRole === 'agent' && '✅ Partner/internal agent: view orders, pay, and upload docs'}
                                    {currentRole === 'customer' && '✅ View own orders, payments, and shipment tracking'}
                                    {currentRole === 'supplier' && '✅ Supplier: view orders, shipments, customs docs, and invoices'}
                                </div>
                            </div>
                        </>
                    ) : null}
                </CardContent>
            </SettingsCard>
        </PageContainer>
    );
};
